import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { CustomImageModal, getOrdinal, MonthRangePicker } from "../CustomFunctions";
import UserDefaultLogo from "../UserDetails/UserDefaultLogo";
import { BASE_URL, IMAGE_URL } from "../../config/axios";
import { StarPerformerIcon } from "../AllSvg";
import Loader from "../Loader";
import dayjs from "dayjs";

const getCurrentMonthYear = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
};

// Reusable Star Icon
const StarIcon = ({ fillPercentage, id }) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
            <linearGradient id={`grad-${id}`} x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset={`${fillPercentage}%`} stopColor="#FFC107" />
                <stop offset={`${fillPercentage}%`} stopColor="#E0E0E0" />
            </linearGradient>
        </defs>
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill={`url(#grad-${id})`} stroke="#FFC107" strokeWidth="1" />
    </svg>
);

// Soft, modern gradients for department cards
const cardGradients = [
    "linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)", // Light Blue
    "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)", // Light Green
    "linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)", // Light Purple
    "linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)", // Light Orange
    "linear-gradient(135deg, #fdf2f8 0%, #fce7f3 100%)", // Light Pink
    "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)", // Light Slate
];

const TopDepartmentsWidget = () => {
    const [loading, setLoading] = useState(false);
    const [departmentsData, setDepartmentsData] = useState([]);
    const [dateRange, setDateRange] = useState(null);
    const [overallTotalMarks, setOverallTotalMarks] = useState(30);

    const [startdate, setStartDate] = useState(getCurrentMonthYear());
    const [enddate, setEndDate] = useState(getCurrentMonthYear());

    // Modal state
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedDepartment, setSelectedDepartment] = useState(null);

    // Animation state
    const [isVisible, setIsVisible] = useState(false);
    const widgetRef = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) {
                setIsVisible(true);
                observer.disconnect(); // only animate once
            }
        }, { threshold: 0.1 });

        if (widgetRef.current) observer.observe(widgetRef.current);
        return () => observer.disconnect();
    }, []);

    const subCompany = sessionStorage.getItem("company_id");

    // Fetch Department Rankings
    useEffect(() => {
        const fetchDepartmentRankings = async () => {
            if (!subCompany || subCompany === "null") return;
            setLoading(true);
            try {
                const params = {
                    sub_company: subCompany,
                    start_month_year: startdate === "null" ? "" : startdate,
                    end_month_year: enddate === "null" ? "" : enddate,
                    sort_order: "high_to_low",
                    employee_status: "active"
                };

                const res = await axios.get(`${BASE_URL}/wfm/star-performance-overall-rating-department-wise/`, { params });

                setDepartmentsData(res.data?.departments || []);
                setDateRange(res.data?.date_range || null);
                setOverallTotalMarks(Number(res.data?.overall_total_marks) || 30);
            } catch (err) {
                console.error("Failed to load department rankings", err);
            } finally {
                setLoading(false);
            }
        };
        fetchDepartmentRankings();
    }, [subCompany, startdate, enddate]);

    const handleDepartmentClick = (dept) => {
        setSelectedDepartment(dept);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedDepartment(null);
    };

    const renderStars = (finalMarks, maxMarks = overallTotalMarks, idPrefix, size = 20) => {
        // 1. Calculate exact proportional score out of 5
        const exactScoreOutOf5 = Math.min(Number(finalMarks || 0) / (maxMarks / 5), 5);

        // 2. Round UP to nearest 0.5 step
        const scoreOutOf5 = Math.ceil(exactScoreOutOf5 * 2) / 2;

        return (
            <div style={{ display: "flex", gap: "2px", alignItems: "center", justifyContent: "center" }}>
                {[1, 2, 3, 4, 5].map((index) => {
                    let fill = 0;

                    // Full star
                    if (scoreOutOf5 >= index) {
                        fill = 100;
                    }
                    // Half star
                    else if (scoreOutOf5 > index - 1) {
                        fill = 50;
                    }

                    return (
                        <StarIcon
                            key={index}
                            id={`${idPrefix}-${index}`}
                            fillPercentage={fill}
                            size={size}
                        />
                    );
                })}
            </div>
        );
    };

    return (
        <div ref={widgetRef} className="db2-card" style={{ display: "flex", flexDirection: "column", width: "44vw", height: "71vh", position: "relative" }}>

            {/* Header */}
            <div className="db2-card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div className="db2-card-title" style={{ margin: 0, flexShrink: 0 }}>
                    <div className="db2-card-icon db2-icon-blue">
                        <StarPerformerIcon />
                    </div>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                        <span>Top Departments</span>
                        {dateRange?.start_date && dateRange?.end_date && (
                            <div style={{ fontSize: "14px", color: "#64748b", marginTop: "4px", fontWeight: "500" }}>
                                {dateRange.start_date} to {dateRange.end_date}
                            </div>
                        )}
                    </div>
                </div>

                {/* Right Aligned Month Filter */}
                <div style={{ flexShrink: 0 }}>
                    <MonthRangePicker
                        startDate={startdate}
                        endDate={enddate}
                        placeHolder1="Start Month"
                        placeHolder2="End Month"
                        maxMonth={dayjs().add(1, "month")}
                        onChange={(start, end) => {
                            setStartDate(start || "null");
                            setEndDate(end || "null");
                        }}
                    />
                </div>
            </div>

            <div className="db2-divider" style={{ margin: 0 }} />

            {/* Grid Area */}
            <div style={{ padding: "15px 0px", display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
                <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden", padding: "5px" }}>
                    {loading ? (
                        <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <Loader container={true} />
                        </div>
                    ) : departmentsData.length === 0 ? (
                        <div style={{ textAlign: "center", padding: "20px", color: "#888", fontSize: "13px" }}>No departments found for this month.</div>
                    ) : (
                        <>
                            {/* ========================================================
                            OLD UI (Grid Cards) - Commented out for reference
                            ======================================================== 
                        <div style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fill, minmax(165px, 1fr))",
                            gap: "12px"
                        }}>
                            {departmentsData.map((dept, index) => {
                                const bgGradient = cardGradients[index % cardGradients.length];

                                return (
                                    <div
                                        key={dept.department_id}
                                        onClick={() => handleDepartmentClick(dept)}
                                        style={{
                                            position: "relative", display: "flex", flexDirection: "column",
                                            alignItems: "center", padding: "20px 10px 15px 10px",
                                            background: bgGradient, border: "1px solid rgba(0,0,0,0.05)",
                                            borderRadius: "12px", cursor: "pointer", transition: "all 0.2s ease",
                                            boxShadow: "0 2px 5px rgba(0,0,0,0.03)"
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.transform = "translateY(-3px)";
                                            e.currentTarget.style.boxShadow = "0 6px 15px rgba(0,0,0,0.08)";
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = "translateY(0)";
                                            e.currentTarget.style.boxShadow = "0 2px 5px rgba(0,0,0,0.03)";
                                        }}
                                    >
                                        <div style={{
                                            width: "55px", height: "55px", borderRadius: "50%",
                                            backgroundColor: "#ffffff", display: "flex", alignItems: "center", 
                                            justifyContent: "center", boxShadow: "0 4px 6px rgba(0,0,0,0.05)",
                                            marginTop: "4px", marginBottom: "12px"
                                        }}>
                                            <span style={{ fontSize: "16px", fontWeight: "800", color: "#0f172a" }}>
                                                {getOrdinal(dept.average_rank)}
                                            </span>
                                        </div>
                                        <span style={{ fontSize: "13px", fontWeight: "700", color: "#1e293b", textAlign: "center", lineHeight: "1.2", marginBottom: "4px" }}>
                                            {dept.department_name}
                                        </span>
                                        <span style={{ fontSize: "11px", color: "#64748b", fontWeight: "600", marginBottom: "16px" }}>
                                            {dept.employee_count} Employees
                                        </span>
                                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%", gap: "5px", padding: "0 5px" }}>
                                            {renderStars(dept.average_overall_final_marks, dept.overall_total_marks, dept.department_id)}
                                            <span style={{ fontSize: "12px", fontWeight: "700", color: "#334155" }}>
                                                Avg: {dept.average_overall_final_marks} <span style={{ color: "#94a3b8", fontWeight: "500", fontSize: "10px" }}>/ {dept.overall_total_marks || overallTotalMarks}</span>
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        */}

                            <div style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "5px",
                                paddingRight: "8px"
                            }}>
                                {departmentsData.map((dept, index) => {
                                    const percentage = Math.min((dept.average_overall_final_marks / (dept.overall_total_marks || overallTotalMarks)) * 100, 100);

                                    {/* 
                                    const shades = ["#2e67e4", "#4e84fa", "#619cfa", "rgb(4 110 202 / 31%)"];
                                    const barColor = shades[index % shades.length]; 
                                    */}

                                    const marks = Number(dept.average_overall_final_marks) || 0;
                                    let barColor;
                                    if (marks <= 5) {
                                        barColor = "rgb(4 110 202 / 31%)"; // Very Light Blue
                                    } else if (marks <= 10) {
                                        barColor = "#73a5f7"; // Light Blue
                                    } else if (marks <= 12) {
                                        barColor = "#6795f8"; // Medium Blue
                                    } else if (marks <= 15) {
                                        barColor = "#5082e0"; // Strong Blue

                                    } else if (marks <= 18) {
                                        barColor = "#6395f3"; // Strong Blue
                                    } else if (marks <= 22) {
                                        barColor = "#5786eb"; // Deep Blue
                                    } else {
                                        barColor = "#2e55c2"; // Darkest Blue
                                    }

                                    return (
                                        <div
                                            key={dept.department_id}
                                            onClick={() => handleDepartmentClick(dept)}
                                            style={{
                                                display: "grid",
                                                gridTemplateColumns: "140px 1fr 45px",
                                                alignItems: "center",
                                                padding: "18px 12px 6px 12px",
                                                cursor: "pointer",
                                                transition: "background-color 0.2s",
                                                backgroundColor: "transparent",
                                                borderRadius: "6px"
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#f8fafc"}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
                                        >
                                            {/* Dept Name */}
                                            <div style={{ paddingRight: "10px", overflow: "hidden" }}>
                                                <span style={{ fontSize: "13px", fontWeight: "700", color: "rgb(120 128 141)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "block" }} title={dept.department_name}>
                                                    {dept.department_name}
                                                </span>
                                            </div>

                                            {/* Flat Bar Track with Tooltip */}
                                            <div style={{ display: "flex", alignItems: "center", paddingRight: "20px" }}>
                                                <div style={{
                                                    width: "100%", height: "18px",
                                                    backgroundColor: "#f1f5f9",
                                                    borderRadius: "8px",
                                                    position: "relative"
                                                }}>
                                                    <div style={{
                                                        width: isVisible ? `${percentage}%` : "0%",
                                                        height: "100%",
                                                        backgroundColor: barColor,
                                                        borderRadius: "8px",
                                                        position: "relative",
                                                        transition: `width 1.2s ease-out ${index * 0.05}s`
                                                    }}>
                                                        {/* Rank Message Icon (Tooltip) */}
                                                        <div style={{
                                                            position: "absolute",
                                                            right: -3,
                                                            top: "-24px",
                                                            transform: "translateX(50%)",
                                                            backgroundColor: barColor,
                                                            color: "#ffffff",
                                                            padding: "2px 7px",
                                                            borderRadius: "4px",
                                                            fontSize: "12px",
                                                            fontWeight: "bold",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            opacity: isVisible ? 1 : 0,
                                                            transition: "opacity 0.4s ease-in-out 0.8s"
                                                        }}>
                                                            {getOrdinal(dept.average_rank)}
                                                            {/* Downward triangle tail */}
                                                            <div style={{
                                                                position: "absolute",
                                                                bottom: "-4px",
                                                                left: "50%",
                                                                transform: "translateX(-50%)",
                                                                width: 0,
                                                                height: 0,
                                                                borderLeft: "4px solid transparent",
                                                                borderRight: "4px solid transparent",
                                                                borderTop: `4px solid ${barColor}`
                                                            }} />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Score (Raw value) */}
                                            <div style={{ textAlign: "right" }}>
                                                <span style={{ fontSize: "13px", fontWeight: "700", color: "#4c5d74" }} title='Average Score'>
                                                    {dept.average_overall_final_marks}/<span style={{ fontSize: "10px" }}>30</span>
                                                </span>
                                            </div>
                                            {/* <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%", gap: "5px", padding: "0 5px" }}>
                                                {renderStars(dept.average_overall_final_marks, dept.overall_total_marks, dept.department_id)}
                                                <span style={{ fontSize: "12px", fontWeight: "700", color: "#334155" }}>
                                                    Avg:{dept.average_overall_final_marks} <span style={{ color: "#94a3b8", fontWeight: "500", fontSize: "10px" }}>/ {dept.overall_total_marks || overallTotalMarks}</span>
                                                </span>
                                            </div> */}

                                            {/* Percentage */}
                                            {/* <div style={{ textAlign: "right" }}>
                                                <span style={{ fontSize: "13px", fontWeight: "700", color: "#64748b" }}>
                                                    {Math.round(percentage)}%
                                                </span>
                                            </div> */}
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Department Employees Modal */}
            {isModalOpen && selectedDepartment && (
                <div style={{
                    position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh",
                    backgroundColor: "rgba(0,0,0,0.5)", zIndex: 9999,
                    display: "flex", alignItems: "center", justifyContent: "center"
                }}>
                    <div style={{
                        backgroundColor: "#fff", width: "500px", maxHeight: "80vh",
                        borderRadius: "12px", display: "flex", flexDirection: "column",
                        boxShadow: "0 10px 25px rgba(0,0,0,0.1)", overflow: "hidden"
                    }}>
                        {/* Modal Header */}
                        <div style={{
                            padding: "20px", borderBottom: "1px solid #e2e8f0",
                            display: "flex", justifyContent: "space-between", alignItems: "center"
                        }}>
                            <div>
                                <h3 style={{ margin: 0, fontSize: "18px", color: "#0f172a" }}>{selectedDepartment.department_name}</h3>
                                <span style={{ fontSize: "13px", color: "#64748b" }}>
                                    {selectedDepartment.employee_count} Employees
                                    {/* {selectedDepartment.average_overall_final_marks} Avg Score */}
                                </span>
                            </div>
                            <button onClick={closeModal} style={{ background: "none", border: "none", fontSize: "24px", cursor: "pointer", color: "#64748b" }}>
                                &times;
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div style={{ padding: "10px 20px", overflowY: "auto", flex: 1 }}>
                            {selectedDepartment.employees && selectedDepartment.employees.length > 0 ? (
                                selectedDepartment.employees.map((emp, index) => (
                                    <div key={index} style={{
                                        display: "flex", alignItems: "center", padding: "12px 0",
                                        borderBottom: index < selectedDepartment.employees.length - 1 ? "1px solid #f1f5f9" : "none"
                                    }}>
                                        <CustomImageModal customStyle>
                                            {emp.profilepic ? (
                                                <img
                                                    src={`${IMAGE_URL}${emp.profilepic}`}
                                                    className="profile-img"
                                                    alt={emp?.name}
                                                />
                                            ) : (
                                                <UserDefaultLogo
                                                    user={{ name: emp.name, profilepic: "" }}
                                                    customStyleforParent={"reqOutDuty-default-logo"}
                                                    customStyleforChild={"small-profile"}
                                                />
                                            )}
                                        </CustomImageModal>
                                        <div style={{ marginLeft: "12px", flex: 1 }}>
                                            <div style={{ fontSize: "14px", fontWeight: "600", color: "#1e293b" }}>{emp.name}</div>
                                            <div style={{ fontSize: "12px", color: "#64748b" }}>{emp.designation}</div>
                                        </div>
                                        <div style={{ textAlign: "right" }}>
                                            <div style={{ fontSize: "14px", fontWeight: "700", color: "#0f172a" }}>
                                                {emp.overall_final_marks}
                                            </div>
                                            <div style={{ fontSize: "11px", color: "#64748b" }}>
                                                Score
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div style={{ textAlign: "center", padding: "30px", color: "#64748b", fontSize: "14px" }}>
                                    No employee details available.
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TopDepartmentsWidget;